import { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { faTimes, faInfoCircle, faCheckCircle, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { Button } from "../App.styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const AlertWrapper = styled.div`
  display: ${({ active }) => (active === "true" ? "flex" : "none")};
  margin-bottom: 1.5rem;
  opacity: ${({ active }) => (active === "true" ? "1" : "0")};
  transition: opacity 0.5s ease-in-out;
  flex-wrap: wrap;
  z-index: 99999;

  &.is-sticky {
    position: fixed;
    top: 10px;
    z-index: 999;
    animation: 500ms ease-in-out 0s normal none 1 running fadeInDown;
  }

  .btn--close-alert {
    padding: 0;
    margin-bottom: 0;
    
    &:hover {
        background-color: transparent;
    }
  }

  .svg-inline--fa {
    font-size: 1rem;
    margin-right: .5rem;
  }

  @media (max-width: ${({ theme }) => theme.maxMobileWidth}) {
    margin-left: 0.25rem;
    margin-right: 0.25rem;
  }
`;

/**
 * A highly customizable alert banner component.
 * Supports different severity colors (error, info, success, warning), sticky positioning 
 * on scroll, auto-closing functionality, and manual dismissal.
 * 
 * @param {Object} props
 * @param {React.ReactNode|string|Array} props.children - The content to display inside the alert.
 * @param {'error'|'info'|'success'|'warning'} props.color - The severity level which determines the icon and background color.
 * @param {string} [props.className=''] - Additional CSS classes.
 * @param {boolean} [props.shouldStick=false] - If true, the alert will become sticky at the top of the viewport when scrolling past it.
 * @param {boolean} [props.closable=false] - If true, renders an 'X' button to manually dismiss the alert.
 * @param {Function} [props.onClose] - Callback fired when the alert is manually closed or auto-closed.
 * @param {boolean} [props.autoClose=false] - If true, automatically fires `onClose` after 10 seconds.
 * @param {string} [props.id] - Optional ID. If provided and not sticky, the window will auto-scroll to the top to ensure the alert is seen.
 * @returns {React.ReactElement} The rendered alert banner.
 */
const Alert = ({ children, color, className = '', shouldStick = false, closable = false, onClose, autoClose = false, id }) => {
    const alertRef = useRef(null)
    const [alertElementTop, setAlertElementTop] = useState(undefined);
    const icons = {
        error: faExclamationTriangle,
        info: faInfoCircle,
        success: faCheckCircle,
        warning: faExclamationTriangle,
    }
    const colors = {
        error: "danger",
        info: "info",
        success: "success",
        warning: "warning",
    }
    useEffect(() => {
        try {
            if (shouldStick) {
                // Set the alert element top when the alert is sticky
                const alertElementEl = alertRef?.current.getBoundingClientRect();
                setAlertElementTop(alertElementEl.top);
            } else if (id !== undefined) {
                // Scroll to the top of the page when the alert is not sticky and has id.
                // We don't want to implement auto scroll for persistent alert without id, 
                // i.e. the ones that are used to display fixed information.
                window.scrollTo({ top: 0, behavior: "smooth" });
            }
        } catch {}
    }, [id, shouldStick]);

    // Auto-hide alert after 10 seconds if autoClose is true
    useEffect(() => {
        if (autoClose) {
            const timer = setTimeout(() => {
                onClose && onClose();
            }, 10000);

            // Cleanup timeout on unmount
            return () => clearTimeout(timer);
        }
    }, [autoClose, onClose]);

    const isSticky = useCallback((e) => {
        const alertElementEl = alertRef?.current;
        const scrollTop = window.scrollY;
        if (scrollTop >= alertElementEl.getBoundingClientRect()?.top - 10) {
            const cs = window.getComputedStyle(alertElementEl.parentElement, null)
            const width = cs.getPropertyValue('width')
            const paddingLeft = cs.getPropertyValue('padding-left')
            const paddingRight = cs.getPropertyValue('padding-right')
            alertElementEl.style.width = `calc(${width} - ${paddingLeft} - ${paddingRight})`
            alertElementEl && alertElementEl.classList.add('is-sticky');
        } else {
            alertElementEl && alertElementEl.classList.remove('is-sticky');
        }
    }, [])
    
    useEffect(() => {
        if (alertElementTop === undefined || !shouldStick) return;
        
        window.addEventListener('scroll', isSticky);
        return () => {
            window.removeEventListener('scroll', isSticky);
        };
    }, [alertElementTop, isSticky, shouldStick]);

    const RenderAlertChildren = ({ content = null }) => {
        if (Array.isArray(content)) {
            return content.map(c => {
                // empty node needs to be rendered as space
                if (c.trim() === '') {
                    return '&nbsp;'
                }
                return c
            }).map((c, id) => (
                <RenderAlertChildren content={c} key={id} />
            ))
        }
        // If the content is a plain string, render as is;
        // otherwise render as HTML (e.g. translated strings that contains foreign characters)
        return content?.props?.children
            ? content
            : <div dangerouslySetInnerHTML={{__html: content}} />
    }

    return (
        <AlertWrapper
            active={String(!!children)}
            className={`alert alert-${colors[color]} ${className} align-items-center`}
            role="alert"
            data-mdb-color={colors[color]}
            ref={alertRef}
        >
            <div className="d-flex flex-grow-1 align-items-center">
                {icons[color] && <FontAwesomeIcon icon={icons[color]} />}
                <RenderAlertChildren content={children} />
            </div>
            {
                closable
                    ? (
                        <Button color="link" onClick={onClose} size="sm" className="btn--close-alert">
                            <FontAwesomeIcon icon={faTimes} />
                        </Button>
                    )
                    : null
            }
        </AlertWrapper>
    )
};

export default Alert;
